import {
	BillingApi,
	Org,
	OrgsApi,
	PartnerApi,
	PartnerContributionTiers,
	Product,
	ProductType,
	UpdateBilling,
} from '@/api';
import { BillingSetupStates } from '../models/billing-setup-loading-states';

export class BillingSetupService {
	public billing: UpdateBilling | null = null;
	public productPartnerOrgs: Org[] | null = null;
	public partnerContributions: PartnerContributionTiers | null = null;
	public directTiers: string[] | null = null;
	public states: BillingSetupStates = {
		initialising: false,
		loadingDirectTiers: false,
		loadingPartnerContributions: false,
		submitting: false,
		errored: false,
	};

	constructor(
		public product: Product,
		private billingApi = new BillingApi(),
		private orgsApi = new OrgsApi(),
		private partnerApi = new PartnerApi(),
	) {}

	public async initialise(): Promise<void> {
		this.states.initialising = true;
		this.billing = await this.loadCurrentBilling(this.product);
		this.directTiers = await this.getDirectTiers();
		this.ensureDirectTierSelected(this.directTiers);
		this.productPartnerOrgs = await this.getAvailablePartners(this.product.type!);
		if (this.billing && this.billing.partnerId) {
			this.partnerContributions = await this.getPartnerContributions(
				this.billing.partnerId!,
				this.product.type!,
			);
		}
		this.states.initialising = false;
	}

	public async updateContributionTier(contributionTier: string): Promise<void> {
		this.billing!.partnerContributionTier = contributionTier;
		this.directTiers = await this.getDirectTiers(); // Direct tiers influenced by partner contribution.
		this.ensureDirectTierSelected(this.directTiers);
	}

	public async updatePartnerId(partnerId: number | null, productType: ProductType): Promise<void> {
		this.billing!.partnerId = partnerId;
		this.partnerContributions = await this.getPartnerContributions(partnerId, productType);
		this.setDefaultContribution();
		this.directTiers = await this.getDirectTiers(); // Direct tiers influenced by partner contribution.
		this.billing!.directTier = this.directTiers[0];
	}

	public async submit(billingParams: UpdateBilling): Promise<boolean> {
		this.states.submitting = true;
		this.states.errored = false;
		try {
			await this.billingApi.updateBilling(billingParams);
			return true;
		} catch (e) {
			this.states.errored = true;
			return false;
		} finally {
			this.states.submitting = false;
		}
	}

	private async loadCurrentBilling(product: Product): Promise<UpdateBilling | null> {
		try {
			const currentBilling = await this.billingApi.currentBilling(product.id!);
			return currentBilling.data.data!;
		} catch {
			return null;
		}
	}

	private async getAvailablePartners(productType: ProductType): Promise<Org[]> {
		try {
			const partners = await this.orgsApi.orgGetBillingPartnersByProductType(productType);
			if (!partners.data.data) {
				return [];
			}
			return partners.data.data;
		} catch {
			return [];
		}
	}

	private async getPartnerContributions(
		partnerId: number | null,
		productType: ProductType,
	): Promise<PartnerContributionTiers | null> {
		this.states.loadingPartnerContributions = true;
		if (partnerId === null) {
			return null;
		}
		try {
			const contributionTiers = await this.partnerApi.getPartnerContributionTiers(
				partnerId,
				productType,
			);
			if (!contributionTiers.data.data) {
				return null;
			}
			return contributionTiers.data.data;
		} catch {
			return null;
		} finally {
			this.states.loadingPartnerContributions = false;
		}
	}

	private async getDirectTiers(): Promise<string[]> {
		if (!this.billing || !this.product.type) {
			return [];
		}
		this.states.loadingDirectTiers = true;
		try {
			const availableTiers = await this.billingApi.getAvailableTiers(this.product.type, {
				data: {
					contributionTier: this.billing.partnerContributionTier
						? this.billing.partnerContributionTier
						: null,
				},
			});
			if (!availableTiers.data.data) {
				return [];
			}
			return availableTiers.data.data;
		} catch {
			return [];
		} finally {
			this.states.loadingDirectTiers = false;
		}
	}

	private setDefaultContribution(): void {
		if (this.billing === null) {
			return;
		}
		if (this.partnerContributions !== null) {
			this.billing.partnerContributionTier = this.partnerContributions.contributionTierDefault;
		} else {
			this.billing.partnerContributionTier = null;
		}
	}

	private ensureDirectTierSelected(directTiers: string[]): void {
		if (
			this.billing &&
			(!this.billing.directTier || !directTiers.includes(this.billing.directTier))
		) {
			this.billing.directTier = directTiers[0];
		}
	}
}
